/**
 * Anomaly Analytics Dashboard Component
 * Displays comprehensive anomaly detection analytics and reporting
 * Implements Requirements: 2.4, 2.5 - Analytics and reporting views
 */

import React, { useState, useEffect } from 'react';
import { adminAnomalyService, AnomalyAnalytics, AnomalyFlagFilters } from '../services/admin-anomaly.service';

interface AnomalyAnalyticsDashboardProps {
  manufacturerId?: string;
  className?: string;
}

const AnomalyAnalyticsDashboard: React.FC<AnomalyAnalyticsDashboardProps> = ({
  manufacturerId,
  className = ''
}) => {
  const [analytics, setAnalytics] = useState<AnomalyAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    loadAnalytics();
  }, [manufacturerId, timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const dateFrom = new Date();
      switch (timeRange) {
        case '7d':
          dateFrom.setDate(dateFrom.getDate() - 7);
          break;
        case '30d':
          dateFrom.setDate(dateFrom.getDate() - 30);
          break;
        case '90d':
          dateFrom.setDate(dateFrom.getDate() - 90);
          break;
      }

      const filters: Pick<AnomalyFlagFilters, 'manufacturerId' | 'dateFrom' | 'dateTo'> = {
        dateFrom,
        dateTo: new Date()
      };

      if (manufacturerId) {
        filters.manufacturerId = manufacturerId;
      }

      const result = await adminAnomalyService.getAnalytics(filters);
      setAnalytics(result);
    } catch (err: any) {
      console.error('Failed to load analytics:', err);
      setError(err.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Failed to load analytics</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <button
            onClick={loadAnalytics}
            className="mt-4 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with Time Range Selector */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Anomaly Detection Analytics</h3>
            <p className="text-sm text-gray-600 mt-1">
              {manufacturerId ? 'Manufacturer-specific' : 'System-wide'} anomaly detection metrics
            </p>
          </div>
          <div className="flex gap-2">
            {(['7d', '30d', '90d'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1 text-sm rounded-md ${
                  timeRange === range
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {range === '7d' ? 'Last 7 days' : range === '30d' ? 'Last 30 days' : 'Last 90 days'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Flags</p>
              <p className="text-3xl font-bold text-gray-900">{analytics.totalFlags}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
              </svg>
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            {analytics.openFlags} open, {analytics.acknowledgedFlags} acknowledged
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">High Risk Flags</p>
              <p className="text-3xl font-bold text-red-600">{analytics.highRiskFlags}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            {analytics.mediumRiskFlags} medium, {analytics.lowRiskFlags} low risk
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Resolution Rate</p>
              <p className="text-3xl font-bold text-green-600">{analytics.flagResolutionRate}%</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            {analytics.acknowledgedFlags + analytics.dismissedFlags} resolved of {analytics.totalFlags}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Risk Score</p>
              <p className="text-3xl font-bold text-orange-600">{analytics.averageRiskScore}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Risk assessment average
          </div>
        </div>
      </div>

      {/* Risk Distribution */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Risk Level Distribution</h4>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-red-500 rounded mr-3"></div>
              <span className="text-sm font-medium text-gray-700">High Risk</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm text-gray-600 mr-2">{analytics.highRiskFlags}</span>
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-red-500 h-2 rounded-full"
                  style={{
                    width: analytics.totalFlags > 0 
                      ? `${(analytics.highRiskFlags / analytics.totalFlags) * 100}%`
                      : '0%'
                  }}
                ></div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-yellow-500 rounded mr-3"></div>
              <span className="text-sm font-medium text-gray-700">Medium Risk</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm text-gray-600 mr-2">{analytics.mediumRiskFlags}</span>
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-yellow-500 h-2 rounded-full"
                  style={{
                    width: analytics.totalFlags > 0 
                      ? `${(analytics.mediumRiskFlags / analytics.totalFlags) * 100}%`
                      : '0%'
                  }}
                ></div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-500 rounded mr-3"></div>
              <span className="text-sm font-medium text-gray-700">Low Risk</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm text-gray-600 mr-2">{analytics.lowRiskFlags}</span>
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{
                    width: analytics.totalFlags > 0 
                      ? `${(analytics.lowRiskFlags / analytics.totalFlags) * 100}%`
                      : '0%'
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Distribution */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Flag Status Distribution</h4>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-red-500 rounded mr-3"></div>
              <span className="text-sm font-medium text-gray-700">Open</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm text-gray-600 mr-2">{analytics.openFlags}</span>
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-red-500 h-2 rounded-full"
                  style={{
                    width: analytics.totalFlags > 0 
                      ? `${(analytics.openFlags / analytics.totalFlags) * 100}%`
                      : '0%'
                  }}
                ></div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-blue-500 rounded mr-3"></div>
              <span className="text-sm font-medium text-gray-700">Acknowledged</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm text-gray-600 mr-2">{analytics.acknowledgedFlags}</span>
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full"
                  style={{
                    width: analytics.totalFlags > 0 
                      ? `${(analytics.acknowledgedFlags / analytics.totalFlags) * 100}%`
                      : '0%'
                  }}
                ></div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-gray-500 rounded mr-3"></div>
              <span className="text-sm font-medium text-gray-700">Dismissed</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm text-gray-600 mr-2">{analytics.dismissedFlags}</span>
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gray-500 h-2 rounded-full"
                  style={{
                    width: analytics.totalFlags > 0 
                      ? `${(analytics.dismissedFlags / analytics.totalFlags) * 100}%`
                      : '0%'
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      {analytics.recentActivity && analytics.recentActivity.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h4>
          <div className="space-y-3">
            {analytics.recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    activity.riskLevel === 'high' ? 'bg-red-500' :
                    activity.riskLevel === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                  }`}></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Product {activity.productId.substring(0, 8)}... - {adminAnomalyService.formatRiskLevel(activity.riskLevel)}
                    </p>
                    <p className="text-xs text-gray-500">
                      Score: {activity.riskScore} | Status: {adminAnomalyService.formatFlagStatus(activity.status)}
                    </p>
                    {activity.reasons.length > 0 && (
                      <p className="text-xs text-gray-600 mt-1">
                        {activity.reasons[0]}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {new Date(activity.createdAt).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnomalyAnalyticsDashboard;